import { Link } from "react-router-dom";
import { ArrowRight, Phone, Shield, Stars, Star, MapPin, BadgeCheck, Clock, Sparkles, Search } from "lucide-react";
import { PlanCard } from "@/components/site/PlanCard";
import { KineticHeading, FadeUp, Reveal, HeroBackdrop } from "@/components/site/motion";
import { PLANS } from "@/lib/plans";
import { BUSINESS, CARRIERS, CARRIER_LOGOS, HIDDEN_CARRIER_TILES } from "@/lib/site";
import { Seo } from "@/components/site/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_coastal-health-plans/artifacts/7pvoz17k_image_68358146.png";
const AGENT_IMG =
  "https://customer-assets.emergentagent.com/job_coastal-health-plans/artifacts/9pkr01rc_SoWal_Life_Pic_color_corrected.jpg";

const TESTIMONIALS = [
  {
    quote:
      "Christian took the mystery out of Medicare for my parents. We compared three plans in plain English and they're now paying $0/month with the same doctors.",
    name: "Linda H.",
    location: "Watercolor, FL",
  },
  {
    quote:
      "Self-employed and dreading the marketplace. Christian found us an ACA plan with a $4,200 subsidy I didn't know I qualified for.",
    name: "Marcus B.",
    location: "Seagrove Beach, FL",
  },
  {
    quote:
      "Local, responsive, and actually answers his phone. Hospital indemnity through Christian saved us thousands when my husband was admitted.",
    name: "Patricia W.",
    location: "Inlet Beach, FL",
  },
];

const FAQS = [
  {
    q: "How much does it cost to work with a broker?",
    a: "Nothing. My services are 100% free to you — insurance carriers compensate me directly when you enroll, and plan pricing is the same whether you go through a broker or not.",
  },
  {
    q: "Are you an independent broker or captive to one company?",
    a: "Fully independent. I represent every major carrier serving Northwest Florida — Florida Blue, Humana, UnitedHealthcare, Aetna, Allstate, HealthSpring and Devoted — so I can shop neutrally on your behalf.",
  },
  {
    q: "Can we meet in person?",
    a: "Absolutely. I'm based in Santa Rosa Beach and meet clients across the 30A corridor, Walton, Bay and Okaloosa counties. Phone, video and in-person consultations are all available.",
  },
  {
    q: "What if I already have a plan?",
    a: "I'll do a free, no-obligation review. If your current plan is the best fit, I'll tell you. If something better exists, I'll show you side-by-side.",
  },
];

const SHOWN_CARRIERS = CARRIERS.filter((c) => !HIDDEN_CARRIER_TILES.includes(c));

const Home = () => {
  return (
    <div data-testid="page-home">
      <Seo
        title="Local Health Insurance & Medicare Broker — Santa Rosa Beach, Destin, PCB"
        description="Independent health insurance broker on Florida's Emerald Coast. Free ACA, Medicare Advantage, Medicare Supplement, Dental and short-term quotes in Santa Rosa Beach, Destin, Miramar Beach, Panama City Beach and Freeport."
        path="/"
      />
      {/* HERO */}
      <section
        className="relative isolate overflow-hidden"
        data-testid="home-hero"
      >
        <HeroBackdrop image={HERO_IMG} />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-24 pb-32 sm:pt-32 sm:pb-40">
          <div className="max-w-3xl">
            <FadeUp delay={0.05}>
              <p className="eyebrow !text-white drop-shadow-[0_1px_3px_rgba(15,36,56,0.6)]">
                <span className="inline-block w-8 h-px bg-white align-middle mr-3" />
                Santa Rosa Beach · Florida & Colorado
              </p>
            </FadeUp>
            <KineticHeading
              delay={0.15}
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight"
              data-testid="home-hero-heading"
              lines={[
                "Health coverage,",
                <span className="text-[var(--brand-secondary)]">tailored to your tide line.</span>,
              ]}
            />
            <FadeUp delay={0.5}>
              <p className="mt-7 text-base sm:text-lg text-slate-100/90 max-w-2xl leading-relaxed">
                30A Health Insurance is an independent brokerage helping individuals,
                families and retirees on the Emerald Coast find ACA, Medicare and
                supplemental plans that actually fit. No call centers, no upsells —
                just an honest comparison with a local who knows your zip code.
              </p>
            </FadeUp>
            <FadeUp delay={0.65} className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/quote"
                data-testid="hero-quote-cta"
                className="cta-kinetic inline-flex items-center justify-center rounded-full bg-white text-[var(--ink)] hover:bg-[var(--brand-secondary)] hover:text-white text-sm font-semibold px-7 h-12"
              >
                Get a free quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                data-testid="hero-call-cta"
                className="cta-kinetic inline-flex items-center justify-center rounded-full border border-white/40 text-white hover:bg-white/10 text-sm font-medium px-7 h-12"
              >
                <Phone className="w-4 h-4 mr-2" /> {BUSINESS.phone}
              </a>
            </FadeUp>

            <FadeUp delay={0.85} className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-slate-100/80">
              <span className="flex items-center gap-2">
                <BadgeCheck className="w-4 h-4 text-[var(--brand-secondary)]" /> 7+ years licensed (FL · CO)
              </span>
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[var(--brand-secondary)]" /> 8 carriers · zero cost to you
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[var(--brand-secondary)]" /> Based on 30A
              </span>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* PLANS GRID */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24" data-testid="home-plans">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="eyebrow">What we offer</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[var(--ink)] tracking-tight">
              Nine focused plan types.<br />
              <span className="text-[var(--brand-primary)]">One brokerage. Your call.</span>
            </h2>
          </div>
          <p className="text-sm text-slate-600 max-w-md leading-relaxed">
            Whether you’re aging into Medicare, navigating ACA after a job change,
            or layering supplemental coverage — we shop every option side by side.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PLANS.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08} className="flex">
              <PlanCard plan={p} />
            </Reveal>
          ))}
        </div>

        {/* Supplemental quiz teaser + dentist check */}
        <Reveal className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Link
            to="/aca-health#aca-indemnity-module"
            data-testid="home-quiz-teaser"
            className="lg:col-span-2 group flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 rounded-2xl border border-slate-200 bg-white px-6 py-5 hover:border-[var(--brand-primary)] transition-colors"
          >
            <span className="inline-flex flex-none items-center justify-center w-11 h-11 rounded-xl bg-[var(--bg-sand)] text-[var(--brand-secondary-dark)]">
              <Sparkles className="w-5 h-5" />
            </span>
            <span className="flex-1">
              <span className="block font-display text-lg text-[var(--ink)]">
                Not sure which supplement fits?
              </span>
              <span className="block text-sm text-slate-600 mt-0.5">
                Take the 30-second quiz — hospital-only indemnity vs. a life + cancer + critical illness bundle.
              </span>
            </span>
            <span className="inline-flex items-center text-sm font-semibold text-[var(--brand-primary)]">
              Take the quiz
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </span>
          </Link>
          <Link
            to="/dental#dentist-check"
            data-testid="home-dentist-check"
            className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-6 py-5 hover:border-[var(--brand-primary)] transition-colors"
          >
            <span className="inline-flex flex-none items-center justify-center w-11 h-11 rounded-xl bg-[var(--bg-secondary)] text-[var(--brand-primary)]">
              <Search className="w-5 h-5" />
            </span>
            <span className="flex-1">
              <span className="block font-display text-lg text-[var(--ink)]">Check your dentist</span>
              <span className="block text-sm text-slate-600 mt-0.5">See which dental plans your office accepts.</span>
            </span>
            <ArrowRight className="w-4 h-4 text-[var(--brand-primary)] transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      {/* WHY US */}
      <section className="bg-[var(--bg-secondary)] py-24 wave-divider" data-testid="home-why">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="eyebrow">Why 30A Health</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[var(--ink)] tracking-tight">
              A local broker who actually answers the phone.
            </h2>
            <p className="mt-6 text-slate-700 leading-relaxed">
              You won’t reach a 1-800 number or a national lead-farm. You’ll reach Christian —
              a {BUSINESS.brokerTitle.toLowerCase()} who lives, works and enrolls clients across
              the 30A corridor and Walton County. Every plan I recommend, I’d put my own
              family on.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { icon: Shield, label: "Independent & unbiased", text: "All 7 major carriers, no preference." },
                { icon: Stars, label: "Hand-built comparisons", text: "Doctors + drugs checked before you sign." },
                { icon: Clock, label: "Year-round support", text: "Renewals, billing, claims help — included." },
                { icon: BadgeCheck, label: "Licensed in 2 states", text: "Florida and Colorado residents welcome." },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="bg-white rounded-xl border border-slate-200 p-5">
                    <Icon className="w-5 h-5 text-[var(--brand-primary)]" />
                    <p className="mt-3 font-display text-base text-[var(--ink)]">{item.label}</p>
                    <p className="mt-1 text-sm text-slate-600">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <img
                src={AGENT_IMG}
                alt="Christian Regalado, Licensed Insurance Broker"
                className="w-full h-[440px] object-cover"
                width="640"
                height="440"
                loading="lazy"
                decoding="async"
                style={{ objectPosition: "center 20%" }}
              />
            </div>
            <div className="absolute -bottom-8 -left-6 bg-white rounded-2xl border border-slate-200 p-6 max-w-xs shadow-sm">
              <p className="eyebrow">Your broker</p>
              <p className="mt-2 font-display text-xl text-[var(--ink)]">Christian Regalado</p>
              <p className="text-sm text-slate-600 mt-1">{BUSINESS.brokerTitle}</p>
              <p className="text-xs text-slate-500 mt-3">{BUSINESS.experience}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CARRIERS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24" data-testid="home-carriers">
        <Reveal>
          <p className="eyebrow text-center">Carriers I represent</p>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl text-[var(--ink)] text-center max-w-2xl mx-auto">
            {SHOWN_CARRIERS.length} of the most trusted health insurance carriers in America.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {SHOWN_CARRIERS.map((c, i) => (
            <Reveal
              key={c}
              delay={i * 0.05}
              y={16}
              className="card-lift border border-slate-200 bg-white rounded-xl p-5 flex items-center justify-center text-center text-sm font-medium text-slate-700 hover:border-[var(--brand-primary)]"
              data-testid={`carrier-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {CARRIER_LOGOS[c] ? (
                <img
                  src={CARRIER_LOGOS[c].src}
                  alt={CARRIER_LOGOS[c].alt}
                  width={CARRIER_LOGOS[c].width}
                  height={CARRIER_LOGOS[c].height}
                  loading="lazy"
                  className="h-7 w-auto max-w-full"
                  data-testid={`carrier-logo-${c.toLowerCase().replace(/\s+/g, "-")}`}
                />
              ) : (
                c
              )}
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white py-24 border-y border-slate-200" data-testid="home-testimonials">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">From clients</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[var(--ink)] max-w-2xl">
              Coverage they trust. Stories they share.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal
                key={i}
                delay={i * 0.1}
                as="figure"
                data-testid={`testimonial-${i}`}
                className="rounded-2xl border border-slate-200 p-7 bg-[var(--bg-primary)] flex flex-col"
              >
                <div className="flex gap-1 text-[var(--brand-secondary)]">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <blockquote className="mt-5 text-slate-700 leading-relaxed flex-1">
                  “{t.quote}”
                </blockquote>
                <figcaption className="mt-6 text-sm">
                  <span className="font-semibold text-[var(--ink)]">{t.name}</span>
                  <span className="text-slate-500"> · {t.location}</span>
                </figcaption>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 sm:px-8 py-24" data-testid="home-faq">
        <Reveal>
          <p className="eyebrow text-center">Common questions</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[var(--ink)] text-center">
            Before we hop on a call.
          </h2>
        </Reveal>
        <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem value={`faq-${i}`} key={i} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-display text-lg text-[var(--ink)] hover:text-[var(--brand-primary)]">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-700 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24" data-testid="home-cta">
        <Reveal className="rounded-3xl bg-[var(--ink)] text-white px-8 sm:px-14 py-16 grid grid-cols-1 lg:grid-cols-3 gap-10 items-center grain overflow-hidden">
          <div className="lg:col-span-2">
            <p className="eyebrow text-[var(--brand-secondary)]">Ready when you are</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Free, no-obligation quote — usually in less than 30 minutes.
            </h2>
            <p className="mt-5 text-slate-300 max-w-2xl">
              Tell us your ZIP and a few details. We’ll compare every plan in your area
              and follow up with options — no spam, no pressure.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              to="/quote"
              data-testid="bottom-quote-cta"
              className="cta-kinetic inline-flex items-center justify-center rounded-full bg-[var(--brand-secondary)] hover:bg-[var(--brand-secondary-dark)] text-[var(--ink)] font-semibold px-7 h-12 transition-colors duration-200"
            >
              Start my quote <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="bottom-call-cta"
              className="cta-kinetic inline-flex items-center justify-center rounded-full border border-white/30 hover:bg-white/10 text-white font-medium px-7 h-12 transition-colors duration-200"
            >
              <Phone className="w-4 h-4 mr-2" /> Call {BUSINESS.phone}
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
